// Worklog Models
// See docs/worklog-editor/03-api-integration.md for API schemas

/**
 * Individual worklog activity from API
 */
export interface WorklogActivity {
  activity_id: string;
  id: string;  // Same as activity_id (for compatibility)
  client_code: string;
  resident_name: string;
  description: string;
  time: string | null;  // HH:MM format
  status: 'Not Started' | 'Started' | 'Completed' | 'Not Doing';
  dateModified?: string;  // For grouping recurring activities
}

/**
 * Resident with their activities from API
 */
export interface WorklogResident {
  resident_name: string;
  client_code: string;
  total_activities: number;
  activities: WorklogActivity[];
}

/**
 * API response for worklog residents endpoint
 */
export interface WorklogApiResponse {
  message: string;
  data: {
    residents: WorklogResident[];
    total_residents: number;
    date_range: {
      from: string;
      to: string;
    };
    environment: string;
  };
  ecase_status_code: number;
}

/**
 * Activity instance from query endpoint
 */
export interface ActivityInstance {
  activity_id: string;
  time: string;
  status: string;
}

/**
 * Activity group from query endpoint
 */
export interface ActivityGroup {
  description: string;
  total_count: number;
  activities: ActivityInstance[];
}

/**
 * API response for activity query endpoint
 */
export interface ActivityQueryResponse {
  message: string;
  data: {
    client_code: string;
    date_range: {
      from: string;
      to: string;
    };
    summary: {
      total_activities: number;
      activity_groups: number;
    };
    activity_groups: ActivityGroup[];
    environment: string;
  };
  ecase_status_code: number;
}

/**
 * API response for max query date endpoint
 */
export interface MaxQueryDateResponse {
  message: string;
  data: {
    client_code: string;
    today: string;
    max_query_date: string;
    max_days_ahead: number;
    environment: string;
  };
  ecase_status_code: number;
}

/**
 * API response for delete activity endpoint
 */
export interface DeleteActivityResponse {
  message: string;
  data: {
    activity_id: string;
    operation: string;
    success: boolean;
    details: {
      success: boolean;
      message?: string;
      error?: string;
      activity_id: string;
    };
  };
  ecase_status_code: number;
}

/**
 * YData structure for DHTMLX Scheduler
 * See docs/worklog-editor/04-scheduler-config.md for details
 */
export interface WorklogYData {
  yunitData: WorklogResidentSection[];
}

/**
 * Resident section in YData (foldable)
 */
export interface WorklogResidentSection {
  key: string;  // client_code
  label: string;  // resident_name
  open: boolean;
  children: WorklogActivityRow[];
}

/**
 * Activity row in YData
 * Grouped by description + dateModified
 */
export interface WorklogActivityRow {
  key: number;  // Integer section ID
  label: string;  // description
  client_code: string;
  description: string;
  dateModified: string;
  isRecurring: boolean;
}

/**
 * Scheduler event structure
 */
export interface WorklogSchedulerEvent {
  id: string;  // activity_id
  start_date: string;  // ISO format
  end_date: string;  // start + 30 mins
  text: string;  // Just the time
  section_id: number;  // Integer section ID - Links to WorklogActivityRow.key

  // Additional metadata
  activity_id: string;
  client_code: string;
  resident_name: string;
  description: string;
  status: 'Not Started' | 'Started' | 'Completed' | 'Not Doing';
  dateModified: string;
}

/**
 * Filter state
 */
export interface WorklogFilterState {
  recurringFilter: 'all' | 'recurring' | 'non-recurring';
  statusFilter: 'pending' | 'all' | 'completed';
  residentFilter: string | null;  // client_code or null for all
}

/**
 * Deletion result
 */
export interface DeletionResult {
  completed: number;
  failed: number;
  total: number;
}

/**
 * Client/Resident information from eCase
 */
export interface EcaseClient {
  ResidentID: number;
  URID: number;
  Customer_Code: string;
  FirstName: string;
  LastName: string;
  Region: string | null;
  Address1: string;
  CaseManager: string | null;
  ResidentStatus: 'Active' | 'Inactive';
}

/**
 * API response for clients endpoint
 */
export interface ClientsApiResponse {
  message?: string;
  data?: EcaseClient[];
  clients?: EcaseClient[];  // Support both formats
}
